import { setEnv } from '@sdk/api/onlineRestApiHandlers'
import { highLevelApi } from '@sdk/main'

type FetchOptions = {
  method?: string
  headers?: Record<string, string>
  body?: string
  credentials?: RequestCredentials
  params?: Record<string, any>
}

const MAX_RETRIES = 5
const BASE_DELAY = 1000 // 1 second
let refreshPromise: Promise<string> | null = null

async function refreshToken(): Promise<string> {
  if (refreshPromise) return refreshPromise

  console.info('Refreshing access token...')
  refreshPromise = (async () => {
    try {
      return await highLevelApi.refreshAccessToken().then((accessTokenResponse: { access_token: string }) =>
        highLevelApi.getEnv().then((env) => {
          setEnv(env)
          return accessTokenResponse.access_token
        }),
      )
    } catch (error) {
      console.error('Failed to refresh token:', error)
      throw error
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// Custom parameter serializer function with 'repeat' format for arrays
function paramsSerializer(params: Record<string, any>): string {
  const searchParams = new URLSearchParams()

  function addParam(key: string, value: any) {
    if (value === undefined || value === null) {
      return
    }
    if (Array.isArray(value)) {
      value.forEach((val) => addParam(key, val))
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      for (const subKey in value) {
        addParam(`${key}[${subKey}]`, value[subKey])
      }
    } else {
      searchParams.append(key, value)
    }
  }

  for (const key in params) {
    addParam(key, params[key])
  }

  return searchParams.toString()
}

async function fetchWithRetry(url: string, options: FetchOptions, remainingRetries = MAX_RETRIES): Promise<any> {
  try {
    const response = await fetch(url, options)
    if (!response.ok) {
      if (response.status === 401) {
        const newToken = await refreshToken()
        options.headers = options.headers || {}
        options.headers.Authorization = `Bearer ${newToken}`
        return fetchWithRetry(url, options, remainingRetries)
      } else if (response.status >= 500 && response.status < 600 && remainingRetries > 0) {
        // Handle server errors with retries
        const delay = BASE_DELAY * Math.pow(2, MAX_RETRIES - remainingRetries)
        await new Promise((resolve) => setTimeout(resolve, delay))
        return fetchWithRetry(url, options, remainingRetries - 1)
      } else {
        // Handle non-2xx and non-5xx status codes without retry
        const errorData = await response.json()
        const error = new Error(
          `HTTP error! status: ${response.status}, message: ${errorData.message || response.statusText}`,
        )
        ;(error as Error & { status?: number }).status = response.status
        throw error
      }
    }

    if (response.status === 204 || response.status === 205) {
      return
    }
    return response.json()
  } catch (error) {
    if (
      remainingRetries > 0 &&
      !(error instanceof TypeError) &&
      (error as Error & { status?: number }).status !== 404
    ) {
      // Handle network errors with retries, but not 404s
      const delay = BASE_DELAY * Math.pow(2, MAX_RETRIES - remainingRetries)
      await new Promise((resolve) => setTimeout(resolve, delay))
      return fetchWithRetry(url, options, remainingRetries - 1)
    }
    throw error
  }
}

export function createHttpClient(origin: string, token: string) {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
  const apiPath = '/api/v1'
  const baseCommon = { credentials: 'include' as RequestCredentials }

  return {
    get: async (endpoint: string, options: FetchOptions = {}) => {
      const url = new URL(`${origin}${apiPath}${endpoint}`)
      if (options.params) {
        url.search = paramsSerializer(options.params)
      }
      const data = await fetchWithRetry(url.toString(), {
        ...baseCommon,
        ...options,
        method: 'GET',
        headers,
      })
      return data
    },
    post: async (endpoint: string, body?: object, options: FetchOptions = {}) => {
      const url = new URL(`${origin}${apiPath}${endpoint}`)
      if (options.params) {
        url.search = paramsSerializer(options.params)
      }
      const data = await fetchWithRetry(url.toString(), {
        ...baseCommon,
        ...options,
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      })
      return data
    },
    put: async (endpoint: string, body: object, options: FetchOptions = {}) => {
      const url = new URL(`${origin}${apiPath}${endpoint}`)
      if (options.params) {
        url.search = paramsSerializer(options.params)
      }
      const data = await fetchWithRetry(url.toString(), {
        ...baseCommon,
        ...options,
        method: 'PUT',
        headers,
        body: JSON.stringify(body),
      })
      return data
    },
    delete: async (endpoint: string, options: FetchOptions = {}) => {
      const url = new URL(`${origin}${apiPath}${endpoint}`)
      if (options.params) {
        url.search = paramsSerializer(options.params)
      }
      const data = await fetchWithRetry(url.toString(), {
        ...baseCommon,
        ...options,
        method: 'DELETE',
        headers,
      })
      return data
    },
    patch: async (endpoint: string, body: object, options: FetchOptions = {}) => {
      const url = new URL(`${origin}${apiPath}${endpoint}`)
      if (options.params) {
        url.search = paramsSerializer(options.params)
      }
      const data = await fetchWithRetry(url.toString(), {
        ...baseCommon,
        ...options,
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      })
      return data
    },
  }
}
